
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ComponentType } from "@/utils/types/componentTypes";
import { IconSelector } from "./IconSelector";

interface ComponentTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, icon: string | null, groupName: string | null) => void;
  componentType: ComponentType | null;
  isLoading: boolean;
  mode: "create" | "edit";
}

export function ComponentTypeDialog({ 
  open, 
  onOpenChange, 
  onSave,
  componentType,
  isLoading,
  mode
}: ComponentTypeDialogProps) {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [internalOpen, setInternalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && componentType) {
        console.log("Editando tipo de componente com ícone:", componentType.icon);
        setName(componentType.name);
        setGroupName(componentType.group_name || "");
        setSelectedIcon(componentType.icon);
      } else {
        setName("");
        setGroupName("");
        setSelectedIcon(null);
      }
      setNameError("");
    }
    setInternalOpen(open);
  }, [open, mode, componentType]);
  
  const validateForm = () => {
    let valid = true;
    
    if (!name.trim()) {
      setNameError("O nome é obrigatório");
      valid = false;
    } else {
      setNameError("");
    }
    
    return valid;
  };
  
  const handleSubmit = () => {
    if (validateForm()) {
      console.log("Salvando tipo de componente com ícone:", selectedIcon, "e grupo:", groupName);
      onSave(name, selectedIcon, groupName || null);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (isLoading) return;
    
    setInternalOpen(newOpen);
    if (!newOpen) {
      // Allow animation to complete before notifying parent
      setTimeout(() => {
        onOpenChange(false);
      }, 100);
    } else {
      onOpenChange(true);
    }
  };
  
  const title = mode === "create" ? "Novo Tipo de Componente" : "Editar Tipo de Componente";
  const description = mode === "create" 
    ? "Adicione um novo tipo de componente." 
    : "Atualize os dados do tipo de componente.";
  
  return (
    <Dialog open={internalOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Nome</Label>
            <Input 
              id="name" 
              value={name} 
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do tipo de componente"
              autoFocus
              disabled={isLoading}
            />
            {nameError && <p className="text-sm text-destructive">{nameError}</p>}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="group_name">Categoria</Label>
            <Input 
              id="group_name" 
              value={groupName} 
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Nome do grupo (opcional)"
              disabled={isLoading}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="icon">Ícone</Label>
            <div className="max-h-80 overflow-y-auto p-2 border rounded-md">
              <IconSelector 
                selectedIcon={selectedIcon} 
                onSelectIcon={(iconPath) => {
                  console.log("Ícone selecionado:", iconPath);
                  setSelectedIcon(iconPath);
                }} 
              />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            onClick={handleSubmit}
            disabled={isLoading}
            className="bg-[#9b87f5] hover:bg-[#9b87f5]/90"
          >
            {isLoading ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
